/**
 * Column add / rename / delete dialogs for the generic Kanban.
 * Domain persistence stays in the consumer callbacks.
 */

import type { ReactNode } from 'react';
import { Button } from '../../atoms/Button';
import { Input } from '../../atoms/Input';
import { Dialog } from '../Dialog';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { kanbanDialogInputClass } from './Kanban.styles';

export interface KanbanManageDialogsProps {
  addOpen: boolean;
  onAddOpenChange: (open: boolean) => void;
  addTitle: string;
  onAddTitleChange: (title: string) => void;
  onAddConfirm: (title: string) => void | Promise<void>;
  /** Disables confirm / Enter while the consumer create is in flight. */
  isAddSubmitting?: boolean;
  /** Optional i18n overrides for the add dialog (defaults stay English). */
  addDialogLabels?: {
    title?: string;
    placeholder?: string;
    confirm?: string;
    cancel?: string;
  };

  renameTarget: { id: string; title: string } | null;
  onRenameTargetClear: () => void;
  renameValue: string;
  onRenameValueChange: (value: string) => void;
  onRenameConfirm: (columnId: string, title: string) => void;

  deleteTargetId: string | null;
  onDeleteTargetClear: () => void;
  deleteReassignId: string | null;
  deleteReassignTitle: ReactNode;
  onDeleteConfirm: (
    columnId: string,
    meta: { reassignToColumnId: string | null }
  ) => void;
}

export function KanbanManageDialogs({
  addOpen,
  onAddOpenChange,
  addTitle,
  onAddTitleChange,
  onAddConfirm,
  isAddSubmitting = false,
  addDialogLabels,
  renameTarget,
  onRenameTargetClear,
  renameValue,
  onRenameValueChange,
  onRenameConfirm,
  deleteTargetId,
  onDeleteTargetClear,
  deleteReassignId,
  deleteReassignTitle,
  onDeleteConfirm,
}: KanbanManageDialogsProps) {
  const addTitleLabel = addDialogLabels?.title ?? 'Add column';
  const addPlaceholder = addDialogLabels?.placeholder ?? 'Column title';
  const addConfirmLabel = addDialogLabels?.confirm ?? 'Add';
  const addCancelLabel = addDialogLabels?.cancel ?? 'Cancel';

  const confirmAdd = async () => {
    const trimmed = addTitle.trim();
    if (!trimmed || isAddSubmitting) return;
    await onAddConfirm(trimmed);
    onAddOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (isAddSubmitting) return;
          onAddOpenChange(open);
        }}
        title={addTitleLabel}
        maxWidth="sm"
      >
        <Dialog.Header title={addTitleLabel} showCloseButton={!isAddSubmitting} />
        <Dialog.Body className="!overflow-visible">
          <Input
            value={addTitle}
            onChange={(event) => onAddTitleChange(event.target.value)}
            placeholder={addPlaceholder}
            autoFocus
            data-autofocus=""
            disabled={isAddSubmitting}
            className={kanbanDialogInputClass}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && addTitle.trim()) {
                event.preventDefault();
                void confirmAdd();
              }
            }}
          />
        </Dialog.Body>
        <Dialog.Footer align="right">
          <Button
            type="outlined"
            intent="secondary"
            size="sm"
            label={addCancelLabel}
            disabled={isAddSubmitting}
            onClick={() => onAddOpenChange(false)}
          />
          <Button
            type="contained"
            intent="primary"
            size="sm"
            label={addConfirmLabel}
            disabled={!addTitle.trim() || isAddSubmitting}
            loading={isAddSubmitting}
            onClick={() => {
              void confirmAdd();
            }}
          />
        </Dialog.Footer>
      </Dialog>

      <Dialog
        open={renameTarget != null}
        onOpenChange={(open) => {
          if (!open) onRenameTargetClear();
        }}
        title="Rename column"
        maxWidth="sm"
      >
        <Dialog.Header title="Rename column" showCloseButton />
        <Dialog.Body className="!overflow-visible">
          <Input
            value={renameValue}
            onChange={(event) => onRenameValueChange(event.target.value)}
            placeholder="Column title"
            autoFocus
            data-autofocus=""
            className={kanbanDialogInputClass}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && renameTarget && renameValue.trim()) {
                onRenameConfirm(renameTarget.id, renameValue.trim());
                onRenameTargetClear();
              }
            }}
          />
        </Dialog.Body>
        <Dialog.Footer align="right">
          <Button
            type="outlined"
            intent="secondary"
            size="sm"
            label="Cancel"
            onClick={onRenameTargetClear}
          />
          <Button
            type="contained"
            intent="primary"
            size="sm"
            label="Save"
            disabled={!renameValue.trim()}
            onClick={() => {
              if (!renameTarget || !renameValue.trim()) return;
              onRenameConfirm(renameTarget.id, renameValue.trim());
              onRenameTargetClear();
            }}
          />
        </Dialog.Footer>
      </Dialog>

      <ConfirmationDialog
        isOpen={deleteTargetId != null}
        onClose={onDeleteTargetClear}
        title="Delete column"
        message={
          deleteReassignId != null ? (
            <span>
              Cards in this column will be moved to{' '}
              <strong>
                {typeof deleteReassignTitle === 'string'
                  ? deleteReassignTitle
                  : 'the adjacent column'}
              </strong>
              .
            </span>
          ) : (
            <span>This is the only column and cannot be deleted.</span>
          )
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: onDeleteTargetClear,
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label: 'Delete',
            onClick: () => {
              if (!deleteTargetId || !deleteReassignId) return;
              onDeleteConfirm(deleteTargetId, {
                reassignToColumnId: deleteReassignId,
              });
              onDeleteTargetClear();
            },
            type: 'contained',
            intent: 'danger',
            disabled: !deleteReassignId,
          },
        ]}
      />
    </>
  );
}
